import { useEffect, useRef, useState, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Plus, Minus, Compass, Loader2, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getMapStyleUrl } from '@/api/geo';

export interface RouteWaypoint {
  index: number;
  lat: number;
  lon: number;
  name: string;
  color: string; // Unique color for this waypoint
  isInRoute?: boolean; // Whether this waypoint is in the active route range
}

export interface RouteLegInfo {
  durationS: number; // seconds
  distanceKm: number; // km
}

interface VisitsRouteMapProps {
  waypoints: RouteWaypoint[];
  routeShape: [number, number][]; // [lon, lat] pairs - GeoJSON format
  legs: RouteLegInfo[]; // Info for each leg (between waypoints)
  isLoading: boolean;
  focusedIndex?: number; // Index of waypoint to focus on (with neighbors)
  zoomedIndex?: number; // Index of waypoint to zoom in closely (single point)
  startIndex?: number; // Start of route range
  endIndex?: number; // End of route range
  onWaypointClick?: (waypoint: RouteWaypoint) => void;
}

export function VisitsRouteMap({
  waypoints,
  routeShape,
  legs,
  isLoading,
  focusedIndex,
  zoomedIndex,
  startIndex,
  endIndex,
  onWaypointClick,
}: VisitsRouteMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const markers = useRef<maplibregl.Marker[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [show3DBuildings, setShow3DBuildings] = useState(false);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current) return;

    // Default center: Minsk
    const defaultCenter: [number, number] = [27.5615, 53.9006];

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: getMapStyleUrl(),
      center: defaultCenter,
      zoom: 10,
      attributionControl: false,
      pitch: 0,
      bearing: 0,
    });

    map.current.on('load', () => {
      setMapLoaded(true);

      // Add route source
      map.current!.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: [],
          },
        },
      });

      // Add route time labels source
      map.current!.addSource('route-labels', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      });

      // Route casing (outline)
      map.current!.addLayer({
        id: 'route-casing',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#1e40af',
          'line-width': 8,
        },
      });

      // Route line
      map.current!.addLayer({
        id: 'route-line',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#3b82f6',
          'line-width': 5,
        },
      });

      // Time labels on route (font inherited from map style)
      map.current!.addLayer({
        id: 'route-time-labels',
        type: 'symbol',
        source: 'route-labels',
        layout: {
          'text-field': ['get', 'label'],
          'text-size': 12,
          'text-anchor': 'center',
          'text-offset': [0, 0],
          'text-allow-overlap': false,
          'symbol-placement': 'point',
        },
        paint: {
          'text-color': '#1f2937',
          'text-halo-color': '#ffffff',
          'text-halo-width': 2,
        },
      });
    });

    return () => {
      map.current?.remove();
      map.current = null;
      setMapLoaded(false);
    };
  }, []);

  // Toggle 3D buildings
  const toggle3DBuildings = useCallback(() => {
    if (!map.current || !mapLoaded) return;

    const newShow3D = !show3DBuildings;
    setShow3DBuildings(newShow3D);

    if (newShow3D) {
      // Add 3D buildings layer if not exists
      if (!map.current.getLayer('3d-buildings')) {
        map.current.addLayer({
          id: '3d-buildings',
          source: 'openmaptiles',
          'source-layer': 'building',
          type: 'fill-extrusion',
          minzoom: 15,
          paint: {
            'fill-extrusion-color': '#aab7c4',
            'fill-extrusion-height': ['get', 'render_height'],
            'fill-extrusion-base': 0,
            'fill-extrusion-opacity': 0.6,
          },
        });
      }
      // Enable pitch
      map.current.easeTo({ pitch: 45, duration: 300 });
    } else {
      // Remove 3D buildings layer
      if (map.current.getLayer('3d-buildings')) {
        map.current.removeLayer('3d-buildings');
      }
      // Reset pitch
      map.current.easeTo({ pitch: 0, duration: 300 });
    }
  }, [mapLoaded, show3DBuildings]);

  // Update route line when shape changes
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    const source = map.current.getSource('route') as maplibregl.GeoJSONSource;
    if (source) {
      if (routeShape.length > 0) {
        source.setData({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: routeShape,
          },
        });
      } else {
        source.setData({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: [],
          },
        });
      }
    }
  }, [routeShape, mapLoaded]);

  // Update time labels when legs/waypoints change
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    const labelsSource = map.current.getSource('route-labels') as maplibregl.GeoJSONSource;
    if (!labelsSource) return;

    // Create label points at midpoints between waypoints
    const features: GeoJSON.Feature[] = [];

    for (let i = 0; i < legs.length && i < waypoints.length - 1; i++) {
      const from = waypoints[i];
      const to = waypoints[i + 1];
      const leg = legs[i];

      // Midpoint
      const midLon = (from.lon + to.lon) / 2;
      const midLat = (from.lat + to.lat) / 2;

      // Format duration
      const minutes = Math.round(leg.durationS / 60);
      const label = minutes < 60 ? `${minutes} мин` : `${Math.floor(minutes / 60)} ч ${minutes % 60} мин`;

      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [midLon, midLat],
        },
        properties: {
          label,
        },
      });
    }

    labelsSource.setData({
      type: 'FeatureCollection',
      features,
    });
  }, [waypoints, legs, mapLoaded]);

  // Update markers when waypoints change
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Remove old markers
    for (const marker of markers.current) {
      marker.remove();
    }
    markers.current = [];

    // Add new markers
    for (let i = 0; i < waypoints.length; i++) {
      const wp = waypoints[i];
      const isStart = i === startIndex;
      const isEnd = i === endIndex;
      const inRoute = wp.isInRoute !== false;

      const el = document.createElement('div');
      el.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          cursor: pointer;
          opacity: ${inRoute ? '1' : '0.4'};
        ">
          <div style="
            width: ${isStart || isEnd ? '32px' : '28px'};
            height: ${isStart || isEnd ? '32px' : '28px'};
            background-color: ${wp.color};
            border: 3px solid ${isStart ? '#22c55e' : isEnd ? '#ef4444' : 'white'};
            border-radius: 50%;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: bold;
            font-size: ${isStart || isEnd ? '14px' : '12px'};
          ">
            ${i + 1}
          </div>
          <div style="
            width: 0;
            height: 0;
            border-left: 6px solid transparent;
            border-right: 6px solid transparent;
            border-top: 8px solid ${wp.color};
            margin-top: -2px;
          "></div>
        </div>
      `;

      // Click handler
      el.addEventListener('click', () => {
        onWaypointClick?.(wp);
      });

      const marker = new maplibregl.Marker({
        element: el,
        anchor: 'bottom',
      })
        .setLngLat([wp.lon, wp.lat])
        .addTo(map.current!);

      markers.current.push(marker);
    }

    // Fit map to markers bounds (only route markers if we have start/end)
    if (waypoints.length > 0) {
      const bounds = new maplibregl.LngLatBounds();
      const routeWaypoints = startIndex !== undefined && endIndex !== undefined
        ? waypoints.filter((_, i) => i >= startIndex && i <= endIndex)
        : waypoints;

      for (const wp of routeWaypoints) {
        bounds.extend([wp.lon, wp.lat]);
      }

      map.current.fitBounds(bounds, {
        padding: { top: 60, bottom: 60, left: 40, right: 40 },
        maxZoom: 15,
        duration: 500,
      });
    }
  }, [waypoints, mapLoaded, onWaypointClick, startIndex, endIndex]);

  // Focus on specific waypoint when focusedIndex changes (shows with neighbors)
  useEffect(() => {
    if (!map.current || !mapLoaded || focusedIndex === undefined || waypoints.length === 0) return;
    if (focusedIndex < 0 || focusedIndex >= waypoints.length) return;

    const bounds = new maplibregl.LngLatBounds();

    // Add focused waypoint
    const focused = waypoints[focusedIndex];
    bounds.extend([focused.lon, focused.lat]);

    // Add previous waypoint if exists
    if (focusedIndex > 0) {
      const prev = waypoints[focusedIndex - 1];
      bounds.extend([prev.lon, prev.lat]);
    }

    // Add next waypoint if exists
    if (focusedIndex < waypoints.length - 1) {
      const next = waypoints[focusedIndex + 1];
      bounds.extend([next.lon, next.lat]);
    }

    map.current.fitBounds(bounds, {
      padding: { top: 80, bottom: 80, left: 60, right: 60 },
      maxZoom: 16,
      duration: 500,
    });
  }, [focusedIndex, waypoints, mapLoaded]);

  // Zoom closely on a single waypoint when zoomedIndex changes
  useEffect(() => {
    if (!map.current || !mapLoaded || zoomedIndex === undefined || waypoints.length === 0) return;
    if (zoomedIndex < 0 || zoomedIndex >= waypoints.length) return;

    const wp = waypoints[zoomedIndex];
    map.current.flyTo({
      center: [wp.lon, wp.lat],
      zoom: 17,
      duration: 500,
    });
  }, [zoomedIndex, waypoints, mapLoaded]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    map.current?.zoomIn();
  }, []);

  const handleZoomOut = useCallback(() => {
    map.current?.zoomOut();
  }, []);

  const handleResetNorth = useCallback(() => {
    map.current?.easeTo({ bearing: 0, pitch: show3DBuildings ? 45 : 0, duration: 300 });
  }, [show3DBuildings]);

  return (
    <div className="w-full h-full relative rounded-lg overflow-hidden" style={{ minHeight: '200px' }}>
      <div ref={mapContainer} className="w-full h-full" />

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-background/50 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      )}

      {/* Map controls */}
      <div className="absolute right-2 top-2 z-10 flex flex-col gap-1">
        <Button
          size="icon"
          variant={show3DBuildings ? 'default' : 'secondary'}
          className="h-8 w-8 shadow-sm"
          onClick={toggle3DBuildings}
          title="3D здания"
        >
          <Building2 className="w-4 h-4" />
        </Button>
        <Button
          size="icon"
          variant="secondary"
          className="h-8 w-8 shadow-sm"
          onClick={handleZoomIn}
        >
          <Plus className="w-4 h-4" />
        </Button>
        <Button
          size="icon"
          variant="secondary"
          className="h-8 w-8 shadow-sm"
          onClick={handleZoomOut}
        >
          <Minus className="w-4 h-4" />
        </Button>
        <Button
          size="icon"
          variant="secondary"
          className="h-8 w-8 shadow-sm"
          onClick={handleResetNorth}
        >
          <Compass className="w-4 h-4" />
        </Button>
      </div>

      {/* Empty state */}
      {waypoints.length === 0 && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center text-muted-foreground bg-background/80 px-4 py-2 rounded-lg">
            <p className="text-sm">Нет точек для отображения</p>
          </div>
        </div>
      )}
    </div>
  );
}
